import type { DataPageModel } from "@modelve/api/models/dataPageModel";
import type { SWRInfiniteResponse } from "swr/infinite";
import type {
  SWRMutationConfiguration,
  SWRMutationResponse,
} from "swr/mutation";
import type { ApiWriteMethod } from "../cache/types";
import type { ApiRetryConfiguration } from "../retryPolicy";
import type { ApiResourceRoute } from "../resources/types";

export type ApiQueryConfig = ApiRetryConfiguration & {
  immutable?: boolean;
  disableCache?: boolean;
};

export type ApiGetConfig = ApiQueryConfig & { withError?: boolean };

export type ApiBatchResult<TEntity> =
  | { result: TEntity; error?: undefined }
  | { result?: undefined; error: ApiBatchError };

export class ApiBatchError extends Error {
  constructor(
    public readonly route: string,
    cause: unknown,
  ) {
    super("Batch API request failed", { cause });
    this.name = "ApiBatchError";
  }
}

export type QueryParams = Record<string, string | boolean | number>;

export type ServerPagedApiGetOptions = ApiQueryConfig & {
  limit?: number;
  queryParams?: QueryParams;
};

export type ServerPagedApiGetResponse<TEntity> = Omit<
  SWRInfiniteResponse<DataPageModel<TEntity>>,
  "data" | "setSize"
> & {
  data: TEntity[] | undefined;
  currentPage: number;
  totalPages: number;
  canReset: boolean;
  canNext: boolean;
  canPrev: boolean;
  prevPage: () => void;
  nextPage: () => void;
  reset: () => void;
};

export type ApiGetFunc<TResponse = any, TEntity = TResponse> = (
  resourceRoute: ApiResourceRoute<TEntity>,
) => Promise<TResponse>;

export type ApiWriteMutationKey = Readonly<{
  namespace: "mutation";
  method: ApiWriteMethod;
}>;

export type ApiWriteMutationArg<TRequest> = {
  resourceRoute: ApiResourceRoute;
  request?: TRequest;
  updateCache: boolean;
};

export type ApiWriteMutationResponse<TResponse, TRequest, TOnRequest> = Omit<
  SWRMutationResponse<
    TResponse,
    unknown,
    ApiWriteMutationKey,
    ApiWriteMutationArg<TRequest>
  >,
  "trigger" | "isMutating"
> & {
  isLoading: boolean;
  onRequest: TOnRequest;
};

export type ApiMutationOptions<TResponse, TRequest = never> = Omit<
  SWRMutationConfiguration<
    TResponse,
    unknown,
    ApiWriteMutationKey,
    ApiWriteMutationArg<TRequest>,
    TResponse
  >,
  "fetcher"
> &
  Readonly<{
    updateCache?: boolean;
  }>;

export type ApiDeleteFunc = (
  resourceRoute: ApiResourceRoute,
  options?: ApiMutationOptions<void>,
) => Promise<void>;

export type ApiPutFunc<TResponse = any, TRequest = any> = (
  resourceRoute: ApiResourceRoute<TResponse>,
  request: TRequest,
  options?: ApiMutationOptions<TResponse, TRequest>,
) => Promise<TResponse>;

export type ApiPostFunc<TResponse = any, TRequest = any> = (
  request: TRequest,
  options?: ApiMutationOptions<TResponse, TRequest>,
) => Promise<TResponse>;

export type ApiPostDynamicFunc<TResponse = any, TRequest = any> = (
  resourceRoute: ApiResourceRoute<TResponse>,
  request: TRequest,
  options?: ApiMutationOptions<TResponse, TRequest>,
) => Promise<TResponse>;
